import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  IMPORT_COLUMNS,
  MAX_IMPORT_BYTES,
  MAX_IMPORT_ROWS,
  buildTemplateCsv,
  checkRows,
  guessMapping,
  mapRows,
  parseCsv,
  todayLocal,
  type ImportColumn,
  type ImportMapping,
  type ImportRow,
  type ImportSheet,
  type ImportTarget,
  type SessionUser,
} from "@iltocat/shared";
import { api, plural } from "../lib/api.js";
import { downloadBlob } from "../lib/download.js";
import { runImport, type RowOutcome } from "../lib/importRun.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import { Icon } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { useToast } from "../components/Toast.js";

/**
 * Bring a lot of things in at once, in four steps: choose a file (or download
 * the template, or load the example), say which column is which, review every
 * row in an editable grid, then import what is ready.
 *
 * The review is the point. Nothing is written until the button, every problem
 * is on the row that has it, and a row with a problem is left out until it is
 * fixed rather than blocking the ones that are fine. A serial that is repeated
 * in the file, or already in stock, is flagged there too.
 *
 * The work itself is `lib/importRun.ts`, through the same routes capture uses.
 */

type Stage = "start" | "map" | "review" | "running";

/** Columns always shown in the grid; the rest appear when the file has something for them. */
const CORE: readonly ImportColumn[] = ["title", "category", "location", "tags", "quantity", "serial", "cost"];

const TARGET_LABELS: Record<"skip" | "field" | "private", string> = {
  skip: "Leave out",
  field: "A custom field",
  private: "A private custom field",
};

export function Import({
  user,
  taxonomy,
  onItems,
}: {
  user: SessionUser;
  taxonomy: Taxonomy;
  onItems: () => void;
}) {
  const stock = user.activeKind === "stock";
  const toast = useToast();

  const [stage, setStage] = useState<Stage>("start");
  const [table, setTable] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ImportMapping[]>([]);
  const [sheet, setSheet] = useState<ImportSheet>({ fields: [], rows: [] });
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const [shelve, setShelve] = useState(true);
  const [receivedOn, setReceivedOn] = useState(todayLocal());
  const [existing, setExisting] = useState<Set<string>>(new Set());
  const [markup, setMarkup] = useState(0);
  const [billable, setBillable] = useState(false);

  const [outcomes] = useState(() => new Map<number, RowOutcome>());
  const [, setTick] = useState(0);
  const [running, setRunning] = useState(false);

  // Two reads, failing separately: a markup that did not load is no reason to
  // stop checking serials, and the other way round.
  const loadExisting = useCallback(async () => {
    if (!stock) return;
    const problems: string[] = [];
    await Promise.all([
      api.stock
        .records()
        .then(({ stock: rows }) =>
          setExisting(new Set(rows.map((row) => row.serial.trim().toLowerCase()).filter(Boolean))),
        )
        .catch(() =>
          problems.push(
            "Could not read the serial numbers already in stock, so those are not being checked.",
          ),
        ),
      api.stock
        .settings()
        .then(({ settings }) => setMarkup(settings.markupPercent))
        .catch(() =>
          problems.push(
            "Could not read the stock markup, so a billed device with no Price is billed at its cost.",
          ),
        ),
    ]);
    if (problems.length > 0) setError(problems.join(" "));
  }, [stock]);

  useEffect(() => {
    void loadExisting();
  }, [loadExisting]);

  const checks = useMemo(
    () => checkRows(sheet, { stock, existingSerials: existing, shelve }),
    [sheet, stock, existing, shelve],
  );
  const ready = useMemo(
    () => sheet.rows.flatMap((_, at) => (!skipped.has(at) && checks[at]?.errors.length === 0 ? [at] : [])),
    [sheet, skipped, checks],
  );
  const undated = sheet.rows.filter(
    (row, at) => !skipped.has(at) && row.client.trim() !== "" && row.deployedOn.trim() === "",
  ).length;
  const flagged = checks.filter((check) => check.errors.length > 0).length;

  if (user.role === "viewer") {
    return (
      <>
        <PageHeader title="Import" />
        <Notice tone="warn">
          You have view-only access to this inventory, so you cannot import into it.
        </Notice>
      </>
    );
  }

  function loadTable(next: string[][]) {
    if (next.length < 2) {
      setError("That file has a header row but nothing under it.");
      return;
    }
    if (next.length - 1 > MAX_IMPORT_ROWS) {
      setError(
        `That file has ${next.length - 1} rows; one import holds at most ${MAX_IMPORT_ROWS}. Split it.`,
      );
      return;
    }
    setError(null);
    setTable(next);
    setMapping(guessMapping(next[0] ?? [], stock));
    setStage("map");
  }

  async function choose(file: File | undefined) {
    if (!file) return;
    if (file.size > MAX_IMPORT_BYTES) {
      setError("That file is over 2 MB, which is more than a CSV of items should be.");
      return;
    }
    loadTable(parseCsv(await file.text()));
  }

  function loadExample() {
    const example = parseCsv(buildTemplateCsv(stock));
    setError(null);
    setTable(example);
    const guessed = guessMapping(example[0] ?? [], stock);
    setMapping(guessed);
    setSheet(mapRows(example, guessed));
    setSkipped(new Set());
    setStage("review");
  }

  function downloadTemplate() {
    downloadBlob(
      new Blob([buildTemplateCsv(stock)], { type: "text/csv;charset=utf-8" }),
      stock ? "utucat-stock-import-template.csv" : "utucat-import-template.csv",
    );
  }

  function confirmMapping() {
    const used = new Map<ImportColumn, number>();
    for (const entry of mapping) {
      if (entry.target === "skip" || entry.target === "field" || entry.target === "private") continue;
      used.set(entry.target, (used.get(entry.target) ?? 0) + 1);
    }
    const twice = [...used.entries()].find(([, count]) => count > 1);
    if (twice) {
      setError(
        `Two columns are both going to ${IMPORT_COLUMNS.find((c) => c.key === twice[0])?.label}. Choose one.`,
      );
      return;
    }
    if (!used.has("title") && !used.has("serial")) {
      setError("Choose a column for Title or Serial number: every row needs one of them.");
      return;
    }
    if (
      mapping.some((entry) => (entry.target === "field" || entry.target === "private") && !entry.label.trim())
    ) {
      setError("A custom field needs a name.");
      return;
    }
    const next = mapRows(table, mapping);
    if (next.rows.length === 0) {
      setError("No rows have anything in the columns you chose.");
      return;
    }
    setError(null);
    setSheet(next);
    setSkipped(new Set());
    setStage("review");
  }

  function edit(at: number, key: ImportColumn, value: string) {
    setSheet((current) => ({
      ...current,
      rows: current.rows.map((row, index) => (index === at ? { ...row, [key]: value } : row)),
    }));
  }

  function editField(at: number, field: number, value: string) {
    setSheet((current) => ({
      ...current,
      rows: current.rows.map((row, index) =>
        index === at ? { ...row, fields: row.fields.map((old, i) => (i === field ? value : old)) } : row,
      ),
    }));
  }

  function toggleSkip(at: number) {
    setSkipped((current) => {
      const next = new Set(current);
      if (!next.delete(at)) next.add(at);
      return next;
    });
  }

  const plan = () => ({
    sheet,
    rows: ready,
    shelve: stock && shelve,
    receivedOn,
    markupPercent: markup,
    billable,
  });

  async function start() {
    outcomes.clear();
    for (const at of ready) outcomes.set(at, { state: "waiting" });
    setStage("running");
    await go();
  }

  async function go() {
    setRunning(true);
    try {
      await runImport(plan(), outcomes, () => setTick((tick) => tick + 1));
    } finally {
      setRunning(false);
      await Promise.all([taxonomy.reload(), loadExisting()]);
    }
    const done = [...outcomes.values()].filter((outcome) => outcome.state === "done").length;
    toast(`Imported ${plural(done, "item")}.`);
  }

  function again() {
    outcomes.clear();
    setTable([]);
    setSheet({ fields: [], rows: [] });
    setSkipped(new Set());
    setError(null);
    setStage("start");
  }

  /* --------------------------------------------------------------- steps -- */

  if (stage === "start") {
    return (
      <>
        <PageHeader
          title="Import"
          meta={<span>Bring in a spreadsheet of things, and review them before anything is saved.</span>}
        />

        {error && <Notice tone="error">{error}</Notice>}

        <Panel
          title="Start from a file"
          note="A CSV from a spreadsheet or another tool. You choose which column is which on the next step."
        >
          <div className="import-actions">
            <label className="button">
              <Icon name="upload" size={18} />
              <span>Choose a CSV file</span>
              <input
                type="file"
                className="sr-only"
                accept=".csv,.tsv,.txt,text/csv,text/plain"
                onChange={(event) => {
                  void choose(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
            </label>
            <button type="button" className="button button-quiet" onClick={downloadTemplate}>
              <Icon name="download" size={18} />
              <span>Download the template</span>
            </button>
            <button type="button" className="button button-quiet" onClick={loadExample}>
              <Icon name="sparkles" size={18} />
              <span>Try an example</span>
            </button>
          </div>
          <p className="muted small">
            The template is a CSV with the columns below and two example rows. Fill it in and choose it here:
            the columns are recognised on sight. &ldquo;Try an example&rdquo; loads those two rows into the
            review step, where you can edit them and import them for real.
          </p>
        </Panel>

        <Panel
          title="The columns"
          note="Any of them can be left out. Anything else in your file can be left out or kept as a field."
        >
          <dl className="import-columns">
            {IMPORT_COLUMNS.filter((column) => stock || !column.stockOnly).map((column) => (
              <div key={column.key}>
                <dt>{column.label}</dt>
                <dd className="muted small">{column.hint}</dd>
              </div>
            ))}
            <div>
              <dt>Field: Warranty ends</dt>
              <dd className="muted small">
                A column headed Field: and a name becomes a custom field on each item.
              </dd>
            </div>
            <div>
              <dt>Private: Vendor</dt>
              <dd className="muted small">
                Headed Private:, it is a field that never leaves this inventory.
              </dd>
            </div>
          </dl>
        </Panel>
      </>
    );
  }

  if (stage === "map") {
    const header = table[0] ?? [];
    const samples = (index: number) =>
      table
        .slice(1, 40)
        .map((row) => (row[index] ?? "").trim())
        .filter(Boolean)
        .slice(0, 2)
        .join("  ·  ");
    const setTarget = (index: number, target: ImportTarget) =>
      setMapping((current) =>
        current.map((entry, i) =>
          i === index
            ? {
                target,
                label:
                  target === "field" || target === "private"
                    ? entry.label || (header[index] ?? "").trim()
                    : "",
              }
            : entry,
        ),
      );

    return (
      <>
        <PageHeader
          title="Which column is which?"
          back={{ label: "Choose another file", onClick: again }}
          meta={
            <span>
              {plural(table.length - 1, "row")} in the file. This is a best guess: change anything that is
              wrong.
            </span>
          }
        />

        {error && <Notice tone="error">{error}</Notice>}

        <Panel>
          <ul className="rows">
            {header.map((name, index) => {
              const entry = mapping[index] ?? { target: "skip" as const, label: "" };
              return (
                <li key={index} className="row-item import-map-row">
                  <span className="row-text">
                    <strong>{name.trim() || `Column ${index + 1}`}</strong>
                    <span className="muted small">{samples(index) || "empty"}</span>
                  </span>
                  <select
                    className="input import-map-select"
                    aria-label={`Where ${name.trim() || `column ${index + 1}`} goes`}
                    value={entry.target}
                    onChange={(event) => setTarget(index, event.target.value as ImportTarget)}
                  >
                    <option value="skip">{TARGET_LABELS.skip}</option>
                    {IMPORT_COLUMNS.filter((column) => stock || !column.stockOnly).map((column) => (
                      <option key={column.key} value={column.key}>
                        {column.label}
                      </option>
                    ))}
                    <option value="field">{TARGET_LABELS.field}</option>
                    <option value="private">{TARGET_LABELS.private}</option>
                  </select>
                  {(entry.target === "field" || entry.target === "private") && (
                    <input
                      className="input import-map-label"
                      aria-label={`Field name for ${name.trim()}`}
                      value={entry.label}
                      onChange={(event) =>
                        setMapping((current) =>
                          current.map((old, i) =>
                            i === index ? { ...old, label: event.target.value } : old,
                          ),
                        )
                      }
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </Panel>

        <div className="import-actions">
          <button type="button" className="button" onClick={confirmMapping}>
            Review the rows
          </button>
        </div>
      </>
    );
  }

  if (stage === "running") {
    const all = [...outcomes.entries()].sort((a, b) => a[0] - b[0]);
    const done = all.filter(([, outcome]) => outcome.state === "done").length;
    const failed = all.filter(([, outcome]) => outcome.state === "failed");
    const finished = !running;

    return (
      <>
        <PageHeader title={finished ? "Import finished" : "Importing…"} />

        <Panel>
          <div
            className="meter"
            role="progressbar"
            aria-label="Import progress"
            aria-valuemin={0}
            aria-valuemax={all.length}
            aria-valuenow={done + failed.length}
          >
            <span style={{ width: `${all.length ? ((done + failed.length) / all.length) * 100 : 0}%` }} />
          </div>
          <p className="import-summary" aria-live="polite">
            {done} of {plural(all.length, "row")} imported
            {failed.length > 0 && `, ${failed.length} could not be`}.
          </p>
        </Panel>

        {failed.length > 0 && (
          <Panel title="Not imported" note="Fix these in the app, or retry if it was the connection.">
            <ul className="rows">
              {failed.map(([at, outcome]) => (
                <li key={at} className="row-item">
                  <span className="row-text">
                    <strong>
                      Row {at + 1}: {sheet.rows[at]?.title || sheet.rows[at]?.serial || "untitled"}
                    </strong>
                    <span className="muted small">{outcome.state === "failed" ? outcome.message : ""}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {finished && (
          <div className="import-actions">
            <button type="button" className="button" onClick={onItems}>
              See the items
            </button>
            {failed.length > 0 && (
              <button type="button" className="button button-quiet" onClick={() => void go()}>
                Retry the ones that failed
              </button>
            )}
            <button type="button" className="button button-quiet" onClick={again}>
              Import another file
            </button>
          </div>
        )}
      </>
    );
  }

  /* --------------------------------------------------------------- review -- */

  const shown = IMPORT_COLUMNS.filter(
    (column) =>
      (!column.stockOnly || stock) &&
      (CORE.includes(column.key) || sheet.rows.some((row) => row[column.key] !== "")),
  ).filter((column) => column.key !== "cost" || stock);

  return (
    <>
      <PageHeader
        title="Review"
        back={{ label: "Start again", onClick: again }}
        meta={
          <span>
            {plural(sheet.rows.length, "row")}: {ready.length} ready
            {flagged > 0 && `, ${flagged} with problems that are left out until fixed`}
            {skipped.size > 0 && `, ${skipped.size} skipped`}.
          </span>
        }
        actions={
          <button type="button" className="button" disabled={ready.length === 0} onClick={() => void start()}>
            <Icon name="upload" size={18} />
            <span>Import {plural(ready.length, "row")}</span>
          </button>
        }
      />

      {error && <Notice tone="error">{error}</Notice>}

      {stock && (
        <Panel title="These are going into a stockroom">
          <label className="import-option">
            <span className="toggle">
              <input type="checkbox" checked={shelve} onChange={(event) => setShelve(event.target.checked)} />
              <span className="toggle-track" />
            </span>
            <span>
              Put them on the shelf as stock. A row with a serial number is one serialized unit; one without
              is a counted line. Costs are private.
            </span>
          </label>
          {shelve && (
            <label className="field import-date">
              <span className="field-label">Came into stock on</span>
              <input
                className="input"
                type="date"
                value={receivedOn}
                onChange={(event) => setReceivedOn(event.target.value)}
              />
            </label>
          )}
          {shelve && sheet.rows.some((row) => row.client.trim() !== "") && (
            <label className="import-option">
              <span className="toggle">
                <input
                  type="checkbox"
                  checked={billable}
                  onChange={(event) => setBillable(event.target.checked)}
                />
                <span className="toggle-track" />
              </span>
              <span>
                Bill the clients for the devices that are already out. Off by default: they went out before
                utucat, so they are probably billed already. The price is the row&rsquo;s Price, or its cost
                plus the {markup}% markup.
              </span>
            </label>
          )}
          {shelve && undated > 0 && (
            <p className="muted small">
              {plural(undated, "row")} with a client {undated === 1 ? "has" : "have"} no Deployed on date, so{" "}
              {undated === 1 ? "it is" : "they are"} dated the day above. That is only a placeholder for a
              device that has been out a while: with billing off it changes no invoice. Fill in Deployed on
              (YYYY-MM-DD) where you know it.
            </p>
          )}
          {!shelve && (
            <p className="muted small">
              They will be plain items; put them on the shelf from each item later.
            </p>
          )}
        </Panel>
      )}

      <div className="import-grid-wrap">
        <table className="table import-grid">
          <thead>
            <tr>
              <th scope="col">Import</th>
              <th scope="col">Row</th>
              {shown.map((column) => (
                <th key={column.key} scope="col">
                  {column.label}
                </th>
              ))}
              {sheet.fields.map((field, index) => (
                <th key={`f${index}`} scope="col">
                  {field.visibility === "private" ? `${field.label} (private)` : field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sheet.rows.map((row: ImportRow, at) => {
              const check = checks[at];
              const bad = (check?.errors.length ?? 0) > 0;
              const off = skipped.has(at) || bad;
              return (
                <Fragment key={at}>
                  <tr className={bad ? "import-row-bad" : off ? "import-row-off" : undefined}>
                    <td data-label="Import">
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={!off}
                          disabled={bad}
                          onChange={() => toggleSkip(at)}
                        />
                        <span className="toggle-track" />
                        <span className="sr-only">Import row {at + 1}</span>
                      </label>
                    </td>
                    <td className="muted" data-label="Row">
                      {at + 1}
                    </td>
                    {shown.map((column) => (
                      <td key={column.key} data-label={column.label}>
                        <input
                          className="input import-cell"
                          aria-label={`${column.label}, row ${at + 1}`}
                          value={row[column.key]}
                          onChange={(event) => edit(at, column.key, event.target.value)}
                        />
                      </td>
                    ))}
                    {sheet.fields.map((field, index) => (
                      <td key={`f${index}`} data-label={field.label}>
                        <input
                          className="input import-cell"
                          aria-label={`${field.label}, row ${at + 1}`}
                          value={row.fields[index] ?? ""}
                          onChange={(event) => editField(at, index, event.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                  {check && (check.errors.length > 0 || check.warnings.length > 0) && (
                    <tr className={bad ? "import-row-bad import-row-notes" : "import-row-notes"}>
                      <td />
                      <td />
                      <td className="import-problems" colSpan={shown.length + sheet.fields.length}>
                        {check.errors.map((message) => (
                          <span key={message} className="import-problem">
                            {message}
                          </span>
                        ))}
                        {check.warnings.map((message) => (
                          <span key={message} className="import-warning">
                            {message}
                          </span>
                        ))}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
